'use client';

/**
 * Live insets for the "finale" reference screen.
 * OWNED BY THE INSETS STAGE — see ../index.tsx for the contract.
 *
 * Covers (reference pixels, 1672×941):
 *   - composer [96,694,1390,77]: a real text input (Enter or the arrow opens the assistant
 *     with the text as seed), placeholder per persona (finale-input / finale-send);
 *   - three prompt tiles (finale-prompt-N) with persona-specific prompts;
 *   - "Сбросить демо" (finale-reset) and "Сменить сцену" (finale-scenes).
 * The orbit, the headline and the offline badge stay as painted.
 */

import { useContext, useEffect, useRef, useState, type CSSProperties, type FormEvent, type KeyboardEvent } from 'react';
import { LivingContext } from '../../context';
import { SCENE_CANVAS, rectStyle, type Bilingual, type Persona, type Rect } from '../../types';
import type { InsetProps } from '../index';
import styles from './finale.module.css';

const CANVAS = SCENE_CANVAS.finale;
const at = (rect: Rect): CSSProperties => rectStyle(rect, CANVAS);
const stagger = (index: number): CSSProperties => ({ '--i': Math.min(index, 5) } as CSSProperties);

const COMPOSER: Rect = [96, 694, 1390, 77];
/** Covers the painted button (centre 1543,728; 56 px) and its glow; the ring is drawn inside at the painted size. */
const SEND: Rect = [1511, 696, 64, 64];
const TILES: readonly Rect[] = [[98, 791, 341, 74], [459, 791, 316, 74], [795, 791, 294, 74]];
const RESET: Rect = [1190, 805, 150, 45];
const SCENES: Rect = [1362, 800, 162, 55];
const MAX_LENGTH = 240;

const PLACEHOLDER: Record<Persona, Bilingual> = {
  teacher: { ru: 'Спросите о сцене, ученике или маршруте', en: 'Ask about a scene, learner or route' },
  expert: { ru: 'Спросите о механике: сигналы, граф, маршрут', en: 'Ask about the mechanics: signals, graph, route' },
  parent: { ru: 'Спросите, что увидит ваш ребёнок', en: 'Ask what your child would see' },
  investor: { ru: 'Спросите, что продукт, а что прототип', en: 'Ask what is product and what is prototype' },
};

const PROMPTS: Record<Persona, readonly [Bilingual, Bilingual, Bilingual]> = {
  teacher: [
    { ru: 'Какие различия в понимании между маршрутом А и В?', en: 'What differs in understanding between routes A and B?' },
    { ru: 'На чём сфокусировать ученика сейчас?', en: 'What should the learner focus on now?' },
    { ru: 'Сделай объяснение проще для этой сцены', en: 'Make the explanation simpler for this scene' },
  ],
  expert: [
    { ru: 'Какие сигналы обновляют состояние ученика?', en: 'Which signals update the learner state?' },
    { ru: 'Как выбирается следующий шаг маршрута?', en: 'How is the next route step chosen?' },
    { ru: 'Где граница между продуктом и прототипами?', en: 'Where is the line between product and prototypes?' },
  ],
  parent: [
    { ru: 'Что ребёнок увидит после ошибки?', en: 'What does a child see after a mistake?' },
    { ru: 'Как понять, что это прогресс, а не баллы?', en: 'How do I know it is progress, not points?' },
    { ru: 'Чем помогает тьютор, а чего не делает?', en: 'What does the tutor help with, and what not?' },
  ],
  investor: [
    { ru: 'Что работает в продукте уже сегодня?', en: 'What works in the product today?' },
    { ru: 'Чем маршрут отличается от списка тем?', en: 'How does a route differ from a topic list?' },
    { ru: 'Какая роль остаётся за учителем?', en: 'What role stays with the teacher?' },
  ],
};

function useLiveFlag(key: string): boolean {
  const first = useRef(key);
  const [live, setLive] = useState(false);
  useEffect(() => {
    if (!live && key !== first.current) setLive(true);
  }, [key, live]);
  return live;
}

function Glyph({ kind }: { kind: 'wave' | 'arrow' | 'route' | 'target' | 'sliders' | 'reset' | 'scenes' }) {
  switch (kind) {
    case 'wave':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 10v4M8 7v10M12 4v16M16 8v8M20 10v4" /></svg>;
    case 'arrow':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 19V5M6 11l6-6 6 6" /></svg>;
    case 'route':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="5" cy="18" r="2" /><circle cx="19" cy="6" r="2" /><path d="M7 17c6 0 4-10 10-10" /></svg>;
    case 'target':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3.5" /><path d="M12 4V2M20 12h2" /></svg>;
    case 'sliders':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" /><circle cx="9" cy="7" r="2" /><circle cx="15" cy="12" r="2" /><circle cx="8" cy="17" r="2" /></svg>;
    case 'reset':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 12a8 8 0 1 1-2.5-5.8M20 4v5h-5" /></svg>;
    case 'scenes':
      return <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="4" y="5" width="16" height="14" rx="3" /><path d="M9 12h6M12 9v6" /></svg>;
  }
}

const TILE_ICONS = ['route', 'target', 'sliders'] as const;

export default function FinaleInsets({ lang, persona, openAssistant, navigate, reset, spotlight, onInteraction }: InsetProps) {
  const living = useContext(LivingContext);
  const reduced = living?.reducedMotion ?? false;
  const ru = lang === 'ru';
  const live = useLiveFlag(persona);
  const [draft, setDraft] = useState('');
  const pick = (copy: Bilingual) => (ru ? copy.ru : copy.en);

  const labels = {
    composer: ru ? 'Вопрос к системе' : 'Question to the system',
    input: ru ? 'Спросить о сцене, ученике или маршруте' : 'Ask about a scene, learner or route',
    hint: ru ? 'Например, сравнить маршруты или изменить глубину объяснений' : 'For example, compare routes or change the explanation depth',
    send: ru ? 'Отправить вопрос' : 'Send question',
    prompts: ru ? 'Быстрые вопросы' : 'Quick prompts',
    reset: ru ? 'Сбросить демо' : 'Reset demo',
    scenes: ru ? 'Сменить сцену' : 'Switch scene',
  };

  const press = (label: string, fn: () => void) => () => {
    onInteraction?.(label);
    fn();
  };

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    const text = draft.trim();
    onInteraction?.(labels.send);
    openAssistant(text.length > 0 ? text : undefined);
    setDraft('');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  const prompts = PROMPTS[persona];

  return (
    <div className={styles.root} data-live={live ? 'true' : undefined} data-reduced={reduced ? 'true' : undefined}>
      <form className={styles.composer} style={at(COMPOSER)} onSubmit={submit} aria-label={labels.composer}>
        <span className={styles.wave} aria-hidden="true"><Glyph kind="wave" /></span>
        <input
          key={persona}
          type="text"
          className={styles.input}
          value={draft}
          placeholder={pick(PLACEHOLDER[persona])}
          aria-label={labels.input}
          data-hotspot="finale-input"
          data-spotlight={spotlight === 'finale-input' ? 'true' : undefined}
          maxLength={MAX_LENGTH}
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="send"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
        />
        <span className={styles.hint}>{labels.hint}</span>
        {spotlight === 'finale-input' && <i className={`${styles.pulse} ${styles.pulseInput}`} aria-hidden="true" />}
      </form>

      <button
        type="button"
        className={styles.send}
        style={at(SEND)}
        data-hotspot="finale-send"
        data-spotlight={spotlight === 'finale-send' ? 'true' : undefined}
        aria-label={labels.send}
        onClick={() => submit()}
      >
        <span className={styles.sendRing}><Glyph kind="arrow" /></span>
        {spotlight === 'finale-send' && <i className={styles.pulse} aria-hidden="true" />}
      </button>

      <div className={styles.tiles} role="group" aria-label={labels.prompts}>
        {TILES.map((rect, index) => {
          const id = `finale-prompt-${index}`;
          const prompt = pick(prompts[index]);
          return (
            <button
              key={id}
              type="button"
              className={styles.tile}
              style={at(rect)}
              data-hotspot={id}
              data-spotlight={spotlight === id ? 'true' : undefined}
              aria-label={prompt}
              onClick={press(prompt, () => openAssistant(prompt))}
            >
              <span className={styles.tileIcon}><Glyph kind={TILE_ICONS[index]} /></span>
              <span key={persona} className={styles.tileText} style={stagger(index)}>{prompt}</span>
              {spotlight === id && <i className={styles.pulse} aria-hidden="true" />}
            </button>
          );
        })}
      </div>

      <button
        type="button"
        className={styles.reset}
        style={at(RESET)}
        data-hotspot="finale-reset"
        data-spotlight={spotlight === 'finale-reset' ? 'true' : undefined}
        aria-label={labels.reset}
        onClick={press(labels.reset, reset)}
      >
        <span className={styles.smallIcon}><Glyph kind="reset" /></span>
        {labels.reset}
        {spotlight === 'finale-reset' && <i className={styles.pulse} aria-hidden="true" />}
      </button>

      <button
        type="button"
        className={styles.scenes}
        style={at(SCENES)}
        data-hotspot="finale-scenes"
        data-spotlight={spotlight === 'finale-scenes' ? 'true' : undefined}
        aria-label={labels.scenes}
        onClick={press(labels.scenes, () => navigate(1))}
      >
        <span className={styles.smallIcon}><Glyph kind="scenes" /></span>
        {labels.scenes}
        {spotlight === 'finale-scenes' && <i className={styles.pulse} aria-hidden="true" />}
      </button>
    </div>
  );
}
